import React, { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';

/**
 * What-If parameter creation dialog. Replaces the chained native prompt() calls
 * in AdvancedTab with a single coherent form.
 */
export function WhatIfParamDialog({ open, onOpenChange, onCreate }) {
  const [name, setName] = useState('');
  const [min, setMin] = useState(0);
  const [max, setMax] = useState(100);
  const [step, setStep] = useState(1);
  const [def, setDef] = useState(50);
  const [suffix, setSuffix] = useState('%');

  useEffect(() => {
    if (open) {
      setName(''); setMin(0); setMax(100); setStep(1); setDef(50); setSuffix('%');
    }
  }, [open]);

  // Auto-derive default to mid of min/max when user hasn't manually changed it
  useEffect(() => {
    if (!Number.isNaN(min) && !Number.isNaN(max)) {
      const mid = Math.round((Number(min) + Number(max)) / 2);
      setDef(prev => (prev === 50 || prev === Math.round(((min + max) / 2)) ? mid : prev));
    }
  }, [min, max]);

  const valid = name.trim() && Number(max) > Number(min) && Number(step) > 0;

  const handleConfirm = () => {
    if (!valid) return;
    onCreate({
      name: name.trim(),
      min: Number(min),
      max: Number(max),
      step: Number(step),
      defaultValue: Number(def),
      suffix,
    });
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="glass-card border-border/50 rounded-2xl sm:max-w-sm" data-testid="whatif-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-base">New What-If Slider</DialogTitle>
        </DialogHeader>
        <div className="space-y-2.5 mt-2">
          <Field label="Parameter name">
            <Input value={name} onChange={e => setName(e.target.value)} placeholder='e.g. Target Loss Ratio' className="rounded-lg bg-muted/50 border-border/50 text-sm h-8" data-testid="whatif-name" />
          </Field>
          <div className="grid grid-cols-3 gap-2">
            <Field label="Min">
              <Input type="number" value={min} onChange={e => setMin(e.target.value)} className="rounded-lg bg-muted/50 border-border/50 text-sm h-8" data-testid="whatif-min" />
            </Field>
            <Field label="Max">
              <Input type="number" value={max} onChange={e => setMax(e.target.value)} className="rounded-lg bg-muted/50 border-border/50 text-sm h-8" data-testid="whatif-max" />
            </Field>
            <Field label="Step">
              <Input type="number" value={step} onChange={e => setStep(e.target.value)} className="rounded-lg bg-muted/50 border-border/50 text-sm h-8" data-testid="whatif-step" />
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <Field label="Default">
              <Input type="number" value={def} onChange={e => setDef(e.target.value)} className="rounded-lg bg-muted/50 border-border/50 text-sm h-8" data-testid="whatif-default" />
            </Field>
            <Field label="Suffix">
              <Input value={suffix} onChange={e => setSuffix(e.target.value)} placeholder='e.g. %' className="rounded-lg bg-muted/50 border-border/50 text-sm h-8" data-testid="whatif-suffix" />
            </Field>
          </div>
        </div>
        <DialogFooter className="mt-4 gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-lg text-xs h-8">Cancel</Button>
          <Button onClick={handleConfirm} disabled={!valid} className="rounded-lg text-xs h-8 bg-primary text-primary-foreground" data-testid="whatif-confirm">
            Create Slider
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-[10px] text-muted-foreground">{label}</Label>
      {children}
    </div>
  );
}
